import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { Button } from '../../components';
import { WebsitePage } from '../website/components/WebsiteShell';
import { ROUTE } from '../../routes';
import { Actions, Content, Glow, Heading, Numeral, Root, Subtitle, TextBlock } from './not-found.styles';

/**
 * Global 404: rendered for any URL that matches no route at all (the router's
 * unconditional `*` catch-all — see `router.tsx`).
 *
 * "Back to home" goes straight to the account home if the owner already has a
 * live brand, even while a separate setup is in progress (e.g. adding a second
 * casino) — otherwise `resolvePostAuthRoute` would prioritise that in-progress
 * setup over the existing live brand. `MainLayout`'s `TopBar` always mounts
 * `BrandSelector`, which dispatches `userBrandsList()` on mount, so this list is
 * already being fetched regardless of how the owner landed here. Until it
 * resolves (or if there's no live brand), fall back to `/` and let
 * `RootRedirect` decide (register / setup / deploy wizard / home) — it waits
 * for the same fetch to settle before ever trusting an empty list.
 */
export const NotFound = (): React.ReactElement => {
  const navigate = useNavigate();
  const hasLiveBrand = useAppSelector((state) => state.casinoInstance.loaded && state.casinoInstance.brands.length > 0);

  const goHome = (): void => navigate(hasLiveBrand ? ROUTE.home() : '/');

  return (
    // Its own page shell, except inside the Website frame (a flagged-off tab's 404), which already has one.
    <WebsitePage header={false}>
      <Root>
        <Glow />
        <Content>
          <Numeral>404</Numeral>
          <TextBlock>
            <Heading text="This page isn't in play." tag="paragraph" />
            <Subtitle
              text="The page you're looking for may have moved, folded, or never existed. Let's get you back to the action."
              tag="paragraph"
            />
          </TextBlock>
          <Actions>
            <Button label="Back to home" onClick={goHome} />
          </Actions>
        </Content>
      </Root>
    </WebsitePage>
  );
};

export default NotFound;
